import {
  ImageStyle,
  Image,
  Text,
  TextStyle,
  View,
  ViewStyle,
  TouchableOpacity,
  ImageSourcePropType,
  Animated,
} from 'react-native';
import React, {useRef, useEffect} from 'react';
import {corner_radius, spacing} from 'theme/Units';
import colors from 'theme/Colors';

// types
export type ToastType = 'success' | 'error' | 'info' | 'warning' | 'default';

export type ToastProps = {
  title: string;
  message: string;
  type?: ToastType;
  duration: number;
  forceClose: () => void;
};

// variants for toast
const toastVariants: Record<
  ToastType,
  {
    icon: ImageSourcePropType; // imported icon for toast
    shadowColor: string; // color of the side border
  }
> = {
  default: {
    icon: require('assets/icons/toast_default@2x.png'),
    shadowColor: 'transparent',
  },
  error: {
    icon: require('assets/icons/failure@2x.png'),
    shadowColor: colors.palette.errorRedDark,
  },
  info: {
    icon: require('assets/icons/info@2x.png'),
    shadowColor: colors.palette.blueInfo,
  },
  success: {
    icon: require('assets/icons/success@2x.png'),
    shadowColor: colors.palette.greenSuccess,
  },
  warning: {
    icon: require('assets/icons/warning@2x.png'),
    shadowColor: colors.palette.yelloWarning,
  },
};

export function Toast(props: Readonly<ToastProps>): JSX.Element {
  const {message, title, type = 'default', forceClose, duration} = props;
  const styles = baseStyle;

  // animation value for slinding in when the component mounts
  const slideAnim = useRef(new Animated.Value(400)).current;

  // animation value for slinding away when the component unmounts
  const opacityAnim = useRef(new Animated.Value(1)).current;

  useEffect(() => {
    // Slide-in animation
    Animated.timing(slideAnim, {
      toValue: 0,
      duration: 300,
      useNativeDriver: true,
    }).start();

    // Fade out and slide out after a delay
    const fadeOutAndSlideOut = () => {
      Animated.parallel([
        Animated.timing(slideAnim, {
          toValue: 400, // Slide out to the right
          duration: 300,
          useNativeDriver: true,
        }),
        Animated.timing(opacityAnim, {
          toValue: 0, // Fade out
          duration: 300,
          useNativeDriver: true,
        }),
      ]).start(() => {
        forceClose();
      });
    };

    const timeout = setTimeout(fadeOutAndSlideOut, duration * 1000);

    return () => clearTimeout(timeout);
  }, [slideAnim, opacityAnim, forceClose, duration]);

  const {icon, shadowColor} = toastVariants[type];

  return (
    <Animated.View
      style={[
        styles.toastBox,
        {
          transform: [{translateX: slideAnim}],
          boxShadow: `-3px 0px 0px 0px ${shadowColor}`,
        },
      ]}>
      <Image source={icon} resizeMode="contain" style={styles.icon} />
      <View style={styles.content}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.message}>{message}</Text>
      </View>
      <TouchableOpacity onPress={forceClose}>
        <Image
          source={require('assets/icons/cross.png')}
          style={styles.closeButton}
        />
      </TouchableOpacity>
    </Animated.View>
  );
}

type styleTypes = {
  toastBox: ViewStyle;
  title: TextStyle;
  message: TextStyle;
  icon: ImageStyle;
  closeButton: ImageStyle;
  content: ViewStyle;
};

const baseStyle: styleTypes = {
  toastBox: {
    padding: spacing['4'],
    backgroundColor: colors.palette.white,
    borderWidth: 1,
    borderRadius: corner_radius['rounded-lg'],
    borderColor: colors.palette.stroke,
    gap: spacing['2'],
    flexDirection: 'row',

  },
  content: {
    gap: spacing['2'],
    maxWidth: 400,
    textWrap: 'wrap',
    flex:1,
  },
  title: {
    fontWeight: '700',
  },
  message: {
    fontWeight: '400',
  },
  closeButton: {
    width: 10,
    height: 10,
  },
  icon: {
    width: 20,
    height: 20,
  },
};
